import React from 'react';
import {
  Firestore,
  getDocs,
  QueryConstraint,
  where,
  limit,
  query,
  collection,
  orderBy,
  QueryDocumentSnapshot,
  startAfter,
  doc,
  getDoc,
} from 'firebase/firestore/lite';
import { useFirestoreClient } from 'src/firebase';
import { Collection } from 'src/models/common';
import { getFieldName } from 'src/models/segmentResult';
import { toSegmentResultFromDoc } from './converter';
import {
  LoginAccountState,
  useLoginAccount,
} from 'src/globalStates/loginAccount';
import { SegmentResultRepository, SegmentResultGetByBroadCastIdQuery, SegmentResultGetAllDeliveryTargetsQuery, DeliveryTarget } from './interface';

export const useSegmentResultRepository = (): SegmentResultRepository => {
  const db = useFirestoreClient();
  const account = useLoginAccount();

  return React.useMemo(() => createSegmentResultRepository(db, account), [db, account]);
};

const createSegmentResultRepository = (db: Firestore, account: LoginAccountState) => {
  return {
    /**
     * セグメント検索結果を配信IDから取得
     */
    async getByBroadCastId({ account, broadCastId }: SegmentResultGetByBroadCastIdQuery) {
      // BroadCastからsegmentResultIdを取得
      const broadCastRef = doc(
        db,
        `${Collection.Merchants}/${account.merchantId!.toString()}/${Collection.BroadCasts}/${broadCastId}`
      );

      const broadCastSnap = await getDoc(broadCastRef);

      if (!broadCastSnap.exists()) {
        return null;
      }

      const segmentResultId = broadCastSnap.data().segmentResultId;

      if (!segmentResultId) {
        return null;
      }

      // SegmentResultを直接取得
      const segmentResultRef = doc(
        db,
        `${Collection.Merchants}/${account.merchantId!.toString()}/${Collection.SegmentResults}/${segmentResultId}`
      );

      const segmentResultSnap = await getDoc(segmentResultRef);

      if (!segmentResultSnap.exists()) {
        return null;
      }

      return toSegmentResultFromDoc(segmentResultSnap.data(), segmentResultSnap.ref);
    },

    async getAllDeliveryTargets({ 
      account, 
      segmentResultId 
    }: SegmentResultGetAllDeliveryTargetsQuery): Promise<DeliveryTarget[]> {
      const lineIdPagesRef = collection(
        db,
        `${Collection.Merchants}/${account.merchantId!.toString()}/${Collection.SegmentResults}/${segmentResultId}/${Collection.LineIdPages}`
      );

      const deliveryTargets: DeliveryTarget[] = [];
      const pageSize = 500;
      let lastDoc: QueryDocumentSnapshot | null = null;
      let hasMore = true;
      let pageCount = 0;

      while (hasMore) {
        pageCount++;

        // クエリを構築
        let q = query(lineIdPagesRef, orderBy('pageIndex'), limit(pageSize));
        
        // 2ページ目以降は前回の最後のドキュメントから続きを取得
        if (lastDoc) {
          q = query(lineIdPagesRef, orderBy('pageIndex'), startAfter(lastDoc), limit(pageSize));
        }

        const snapshot = await getDocs(q);

        // データがない場合は終了
        if (snapshot.empty) {
          hasMore = false;
          break;
        }

        // 各ドキュメントからdeliveryTargetsを抽出
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.deliveryTargets && Array.isArray(data.deliveryTargets)) {
            deliveryTargets.push(...data.deliveryTargets);
          }
        });


        // 次のページがあるかチェック
        if (snapshot.docs.length < pageSize) {
          hasMore = false;
        } else {
          lastDoc = snapshot.docs[snapshot.docs.length - 1];
        }
      }
      return deliveryTargets;
    },
  };
};